import axios from 'axios';
import { getUserApiKeys, getUserOrganization } from './supabase';
import { cacheService, CACHE_KEYS, DEFAULT_CACHE_TTL } from './cache';

const API_GATEWAY_CONFIG = {
  baseURL: process.env.REACT_APP_API_GATEWAY_URL,
  timeout: 60000, 
  headers: {
    'Content-Type': 'application/json'
  }
};

// Log configuration for debugging
console.log('🌐 API Gateway Configuration:', {
  baseURL: API_GATEWAY_CONFIG.baseURL,
  timeout: API_GATEWAY_CONFIG.timeout,
  envVar: process.env.REACT_APP_API_GATEWAY_URL
});

// Warn if baseURL is not set
if (!API_GATEWAY_CONFIG.baseURL) {
  console.error('⚠️ WARNING: API Gateway baseURL is not set! Check your .env file and restart the dev server.');
  console.error('⚠️ Expected: REACT_APP_API_GATEWAY_URL=https://062uo3ijtj.execute-api.us-east-1.amazonaws.com/prod');
}

// Create axios instance with API Gateway config
const api = axios.create(API_GATEWAY_CONFIG);

// Get the current API configuration
export const getCurrentApiConfig = () => {
  return API_GATEWAY_CONFIG;
};

// Set the API configuration (kept for compatibility)
export const setApiConfig = (config) => {
  console.log('Using API Gateway config');
};

/**
 * Clear cached API key from storage
 */
export const clearCachedApiKey = () => {
  localStorage.removeItem('apiKey');
  sessionStorage.removeItem('apiKey');
  console.log('🗑️ Cleared cached API key from storage');
};

/**
 * Get the user's API key from Supabase
 * @param {string} userId - Clerk user ID
 * @param {boolean} forceRefresh - Force refresh from Supabase, ignoring cache
 * @returns {Promise<string|null>} - API key or null if not found
 */
export const getUserApiKey = async (userId, forceRefresh = false) => {
  try {
    if (!userId) {
      console.log('No user ID provided for API key retrieval');
      return null;
    }

    // If force refresh, clear cache first
    if (forceRefresh) {
      clearCachedApiKey();
    }

    // First check localStorage/sessionStorage for cached API key
    const cachedKey = localStorage.getItem('apiKey') || sessionStorage.getItem('apiKey');
    if (cachedKey && !forceRefresh) {
      // Validate that cached key is not just a prefix (which won't work for auth)
      if (cachedKey.endsWith('...')) {
        console.log('⚠️ Cached key is a prefix, clearing and fetching fresh');
        clearCachedApiKey();
      } else {
        console.log('Using cached API key from storage');
        return cachedKey;
      }
    }

    // Get API keys from Supabase
    const apiKeys = await getUserApiKeys(userId);
    
    if (apiKeys && apiKeys.length > 0) {
      // Get the most recent active API key
      const activeKey = apiKeys.find(key => key.is_active);
      if (activeKey) {
        // Note: We can't return the full key from Supabase as it's not stored there
        // The backend should handle authentication via X-User-ID header
        // For now, we'll use the prefix but the backend should validate based on user ID
        console.log('Retrieved API key info from Supabase');
        // Don't cache the prefix as it won't work for authentication
        // Return null to let the backend handle auth via X-User-ID
        return null;
      }
    }

    console.log('No active API key found for user');
    return null;
  } catch (error) {
    console.error('Error getting user API key:', error);
    return null;
  }
};

// Original uncached version
const _fetchProjectsUncached = async (apiKey = null, userId = null) => {
  try {
    const headers = {};
    
    // Get the user's API key if not provided
    let keyToUse = apiKey;
    if (!keyToUse && userId) {
      keyToUse = await getUserApiKey(userId);
    }
    
    // Fallback to hardcoded key if no user key available
    if (!keyToUse) {
      console.log('No user API key available, using fallback key');
      keyToUse = 'arms_******************';
    }
    
    headers['Authorization'] = `Bearer ${keyToUse}`;
    
    // Add user ID header for organization filtering
    if (userId) {
      headers['X-User-ID'] = userId;
      console.log('Adding X-User-ID header for organization filtering:', userId);
    }
    
    const response = await api.get('/app-data', {
      headers: headers
    });
    console.log('📊 fetchProjects response summary:', {
      hasData: !!response.data,
      hasProjects: !!response.data?.projects,
      projectsCount: response.data?.projects?.length || 0,
      firstProject: response.data?.projects?.[0]?.project || 'N/A',
      count: response.data?.count || 0
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching projects. Please try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please provide a valid API key.');
    }
    throw new Error(error.message || 'Failed to fetch projects');
  }
};

// Cache-enabled version
export const fetchProjects = async (apiKey = null, userId = null, useCache = true) => {
  if (!useCache || !userId) {
    return _fetchProjectsUncached(apiKey, userId);
  }

  const cacheKey = CACHE_KEYS.PROJECTS(userId);
  return cacheService.cachedApiCall(
    cacheKey,
    () => _fetchProjectsUncached(apiKey, userId),
    DEFAULT_CACHE_TTL.projects
  );
};

// Original uncached version
const _fetchRunsUncached = async (userId = null) => {
  try {
    console.log('🔄 fetchRuns called with userId:', userId);
    const headers = {};
    
    // Get the user's API key
    let apiKey = null;
    if (userId) {
      console.log('🔑 Getting API key for user:', userId);
      apiKey = await getUserApiKey(userId);
    }
    
    // Fallback to hardcoded key if no user key available
    if (!apiKey) {
      console.log('⚠️ No user API key available, using fallback key');
      apiKey = 'arms_3ZShUkZ9qrZz3JeMgkSpACDL';
    }
    
    headers['Authorization'] = `Bearer ${apiKey}`;
    
    // Add user ID header for organization filtering
    if (userId) {
      headers['X-User-ID'] = userId;
      console.log('👤 Adding X-User-ID header for organization filtering:', userId);
    }
    
    console.log('📡 Making API call to /app-data with headers:', Object.keys(headers));
    console.log('⏱️ Using timeout:', API_GATEWAY_CONFIG.timeout);
    
    // Use the same endpoint as fetchProjects since there's no separate /runs endpoint
    const response = await api.get('/app-data', {
      headers: headers,
      timeout: 60000
    });
    
    console.log('✅ fetchRuns successful, data count:', response.data.count || 0);
    console.log('📊 fetchRuns response summary:', {
      hasData: !!response.data,
      hasProjects: !!response.data?.projects,
      projectsCount: response.data?.projects?.length || 0,
      firstProject: response.data?.projects?.[0]?.project || 'N/A',
      firstProjectRunCount: response.data?.projects?.[0]?.runs?.length || 0
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching runs:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    
    if (error.code === 'ECONNABORTED') {
      console.error('⏰ Request timed out - this might indicate a network or server issue');
      throw new Error('Request timed out while fetching runs. Please try again.');
    }
    
    if (error.response) {
      console.error('📊 Response status:', error.response.status);
      console.error('📄 Response data:', error.response.data);
    }
    
    throw new Error(error.message || 'Failed to fetch runs');
  }
};

// Cache-enabled version
export const fetchRuns = async (userId = null, useCache = true) => {
  if (!useCache || !userId) {
    return _fetchRunsUncached(userId);
  }

  const cacheKey = CACHE_KEYS.RUNS(userId);
  return cacheService.cachedApiCall(
    cacheKey,
    () => _fetchRunsUncached(userId),
    DEFAULT_CACHE_TTL.runs
  );
};

// Original uncached version
const _fetchProjectDetailsUncached = async (projectId, userId = null, retryWithoutCache = false) => {
  try {
    console.log('🔄 fetchProjectDetails called with projectId:', projectId, 'userId:', userId);
    const headers = {};
    
    // Get the user's API key (force refresh if retrying after 403)
    let apiKey = null;
    if (userId) {
      console.log('🔑 Getting API key for user:', userId);
      apiKey = await getUserApiKey(userId, retryWithoutCache);
    }
    
    // Only use API key if it's a valid full key (not a prefix)
    // The backend should primarily authenticate via X-User-ID header for organization access
    if (apiKey && !apiKey.endsWith('...')) {
      headers['Authorization'] = `Bearer ${apiKey}`;
      console.log('🔑 Using user API key for authentication');
    } else {
      // Use fallback key - backend should validate organization membership via X-User-ID
      // The API key is for request authentication, X-User-ID is for organization authorization
      console.log('⚠️ No valid user API key available, using fallback key');
      apiKey = 'arms_3ZShUkZ9qrZz3JeMgkSpACDL';
      headers['Authorization'] = `Bearer ${apiKey}`;
      if (retryWithoutCache) {
        console.log('🔄 Retry after 403: Backend should validate organization via X-User-ID header');
      }
    }
    
    // Add user ID header for organization filtering (this is the primary auth method)
    if (userId) {
      headers['X-User-ID'] = userId;
      console.log('👤 Adding X-User-ID header for organization filtering:', userId);
    }
    
    console.log('📡 Making API call to:', `/get-project-details/${projectId}`);
    console.log('📋 Headers:', Object.keys(headers));
    
    const response = await api.get(`/get-project-details/${projectId}`, {
      headers: headers,
      timeout: 60000
    });
    
    console.log('✅ API response received:', response);
    console.log('📄 Response data:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching project details:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    
    if (error.response) {
      console.error('📊 Response status:', error.response.status);
      console.error('📄 Response data:', error.response.data);
    }
    
    // Handle 403 errors by clearing cache and retrying once
    if (error.response?.status === 403 && !retryWithoutCache && userId) {
      console.log('🔄 403 error detected, clearing API key cache and retrying...');
      clearCachedApiKey();
      // Clear project details cache for this user
      const cacheKey = CACHE_KEYS.PROJECT_DETAILS(projectId, userId);
      cacheService.remove(cacheKey);
      // Retry once without cache
      return _fetchProjectDetailsUncached(projectId, userId, true);
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching project details. Please try again.');
    }
    if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to access this project. Please check your organization membership.');
    }
    if (error.response?.status === 404) {
      throw new Error('Project not found.');
    }
    throw new Error(error.message || 'Failed to fetch project details');
  }
};

// Cache-enabled version
export const fetchProjectDetails = async (projectId, userId = null, useCache = true) => {
  if (!useCache || !userId) {
    return _fetchProjectDetailsUncached(projectId, userId);
  }

  const cacheKey = CACHE_KEYS.PROJECT_DETAILS(projectId, userId);
  return cacheService.cachedApiCall(
    cacheKey,
    () => _fetchProjectDetailsUncached(projectId, userId, false),
    DEFAULT_CACHE_TTL.projectDetails
  );
};

// Cache management utilities
export const clearUserCache = (userId) => {
  cacheService.clearUserCache(userId);
  console.log('🗑️ Cleared cache for user:', userId);
};

export const refreshProjectsCache = async (userId) => {
  const cacheKey = CACHE_KEYS.PROJECTS(userId);
  cacheService.remove(cacheKey);
  console.log('🔄 Refreshed projects cache for user:', userId);
  return fetchProjects(null, userId, true);
};

export const refreshRunsCache = async (userId) => {
  const cacheKey = CACHE_KEYS.RUNS(userId);
  cacheService.remove(cacheKey);
  console.log('🔄 Refreshed runs cache for user:', userId);
  return fetchRuns(userId, true);
};

export const refreshProjectDetailsCache = async (projectId, userId) => {
  const cacheKey = CACHE_KEYS.PROJECT_DETAILS(projectId, userId);
  cacheService.remove(cacheKey);
  console.log('🔄 Refreshed project details cache for project:', projectId, 'user:', userId);
  return fetchProjectDetails(projectId, userId, true);
};

export const getCacheStats = () => {
  return cacheService.getStats();
};

export const checkDynamoDBStatus = async () => {
  try {
    const response = await api.get('/dynamodb-status', {
      timeout: 60000
    });
    return response.data;
  } catch (error) {
    console.error('Error checking DynamoDB status:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Connection timed out. Please check your network connection and try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('Invalid AWS credentials. Please check your access key and secret key.');
    }
    if (error.response?.status === 404) {
      throw new Error('Could not connect to the DynamoDB table. Please verify the table name is correct.');
    }
    throw new Error(error.message || 'Failed to connect to DynamoDB. Please check your configuration.');
  }
};

export const fetchAuditLogs = async () => {
  try {
    const response = await api.get('/audit-logs');
    return response.data;
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

// New API functions for Lambda endpoints
export const generateApiKey = async (projectId, userId) => {
  try {
    const response = await api.post('/generate-api-key', {
      project_id: projectId
    }, {
      headers: {
        'X-User-ID': userId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error generating API key:', error);
    throw new Error(error.message || 'Failed to generate API key');
  }
};

export const listApiKeys = async (userId) => {
  try {
    const response = await api.get('/list-api-keys', {
      headers: {
        'X-User-ID': userId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing API keys:', error);
    throw new Error(error.message || 'Failed to list API keys');
  }
};

export const verifyApiKey = async (apiKey) => {
  try {
    const response = await api.post('/verify-api-key', {
      api_key: apiKey
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying API key:', error);
    throw new Error(error.message || 'Failed to verify API key');
  }
};

export const revokeApiKey = async (keyId) => {
  try {
    const response = await api.delete(`/revoke-api-key/${keyId}`);
    return response.data;
  } catch (error) {
    console.error('Error revoking API key:', error);
    throw new Error(error.message || 'Failed to revoke API key');
  }
};

export const updateKeyUsage = async (apiKey, usageData) => {
  try {
    const response = await api.post('/update-key-usage', {
      api_key: apiKey,
      usage_data: usageData
    });
    return response.data;
  } catch (error) {
    console.error('Error updating key usage:', error);
    throw new Error(error.message || 'Failed to update key usage');
  }
};

export const getUserOrganizations = async (userId) => {
  try {
    const response = await api.get('/get-user-organizations', {
      headers: {
        'X-User-ID': userId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting user organizations:', error);
    throw new Error(error.message || 'Failed to get user organizations');
  }
};

export const getOrganizationMembers = async (orgId, userId) => {
  try {
    const response = await api.get('/get-organization-members', {
      headers: {
        'X-User-ID': userId
      },
      params: {
        organization_id: orgId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting organization members:', error);
    throw new Error(error.message || 'Failed to get organization members');
  }
};

export const syncOrganization = async (organizationId) => {
  try {
    const response = await api.post('/sync-organization', {
      organization_id: organizationId
    });
    return response.data;
  } catch (error) {
    console.error('Error syncing organization:', error);
    throw new Error(error.message || 'Failed to sync organization');
  }
};

export const getProtectedData = async (userId) => {
  try {
    const response = await api.get('/get-protected-data', {
      headers: {
        'X-User-ID': userId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting protected data:', error);
    throw new Error(error.message || 'Failed to get protected data');
  }
};

/**
 * Fetch traces and spans from DynamoDB
 * @param {string|string[]} traceIds - Single trace ID or array of trace IDs
 * @param {string} projectId 
 * @param {string} userId - User ID for authentication
 * @returns {Promise<Object>} - Traces and spans data
 */
export const fetchTraces = async (traceIds, projectId, userId = null) => {
  try {
    console.log('🔄 fetchTraces called with:', { traceIds, projectId, userId, baseURL: API_GATEWAY_CONFIG.baseURL });
    
    const headers = {};
    
    // Get the user's API key
    let apiKey = null;
    if (userId) {
      apiKey = await getUserApiKey(userId);
    }
    
    // Fallback to hardcoded key if no user key available
    if (!apiKey) {
      console.warn('⚠️ No user API key available, using fallback key');
      apiKey = 'arms_3ZShUkZ9qrZz3JeMgkSpACDL';
    }
    
    headers['Authorization'] = `Bearer ${apiKey}`;
    
    // Add user ID header for organization filtering
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    // Convert traceIds to array if it's a single string
    const traceIdsArray = Array.isArray(traceIds) ? traceIds : [traceIds];
    
    // Handle DynamoDB format: [{"S":"trace_id"}]
    const parsedTraceIds = traceIdsArray.map(tid => {
      if (typeof tid === 'object' && tid !== null && 'S' in tid) {
        return tid.S;
      }
      return tid;
    });
    
    const params = {
      trace_ids: JSON.stringify(parsedTraceIds),
      project_id: projectId
    };
    
    console.log('📡 Making API call to /get-traces with params:', params);
    console.log('📋 Headers:', Object.keys(headers));
    
    const response = await api.get('/get-traces', {
      headers: headers,
      params: params,
      timeout: 60000
    });
    
    console.log('✅ fetchTraces response received:', {
      status: response.status,
      dataKeys: response.data ? Object.keys(response.data) : null,
      hasTraces: response.data?.data?.traces ? response.data.data.traces.length : 0
    });
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching traces:', error);
    console.error('❌ Error details:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      baseURL: error.config?.baseURL
    });
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching traces. Please try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please provide a valid API key.');
    }
    if (error.response?.status === 404) {
      throw new Error('Traces not found. Please verify the trace IDs and project name.');
    }
    if (error.response?.status === 403) {
      throw new Error('Access denied. Please check your API key permissions.');
    }
    
    // Include more details in error message for debugging
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch traces';
    throw new Error(errorMessage);
  }
};

/**
 * Fetch spans for a specific trace
 * @param {string} traceId - Trace ID
 * @param {string} projectId - Project Name (e.g., "HR Policy") - NOT the project ID
 * @param {string} userId - User ID for authentication
 * @returns {Promise<Object>} - Spans data
 */
export const fetchSpans = async (traceId, projectId, userId = null) => {
  try {
    const headers = {};
    
    // Get the user's API key
    let apiKey = null;
    if (userId) {
      apiKey = await getUserApiKey(userId);
    }
    
    // Fallback to hardcoded key if no user key available
    if (!apiKey) {
      apiKey = 'arms_3ZShUkZ9qrZz3JeMgkSpACDL';
    }
    
    headers['Authorization'] = `Bearer ${apiKey}`;
    
    // Add user ID header for organization filtering
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    const response = await api.get('/get-traces', {
      headers: headers,
      params: {
        trace_id: traceId,
        project_id: projectId
      },
      timeout: 60000
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching spans:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching spans. Please try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please provide a valid API key.');
    }
    throw new Error(error.message || 'Failed to fetch spans');
  }
}; 